import {Session} from "@m2c2kit/session";
import {ColorShapes} from "./index";

const activity = new ColorShapes();

// Set game parameters from URL parameters
const urlParams = new URLSearchParams(window.location.search);
const number_of_trials_count = 5; // 시도 횟수
const gameParameters: { [key: string]: string } = {
    "number_of_trials": number_of_trials_count.toString()
};
urlParams.forEach((value, key) => {
    gameParameters[key] = value;
});
activity.setParameters(gameParameters);

const session = new Session({
    activities: [activity],
});

// 개별 도형(Shape)에 대한 타입 정의
type Shape = {
    shape_index: number;
    color_name: string;
    rgba_color: [number, number, number, number]; // RGBA 색상값
    location: {
        row: number;
        column: number;
    };
};

type DeviceMetadata = {
    userAgent: string;
    devicePixelRatio: number;
    screen: {
        availHeight: number;
        availWidth: number;
        colorDepth: number;
        height: number;
        orientation: {
            type: string;
            angle: number;
        };
        pixelDepth: number;
        width: number;
    };
    webGlRenderer: string;
};

// 하나의 실험(Trial)에 대한 타입 정의
type Trial = {
    document_uuid: string;
    study_id: string | null;
    study_uuid: string | null;
    session_uuid: string;
    activity_uuid: string;
    activity_id: string;
    activity_publish_uuid: string;
    activity_version: string;
    device_timezone: string;
    device_timezone_offset_minutes: number;
    locale: string;
    activity_begin_iso8601_timestamp: string;
    trial_begin_iso8601_timestamp: string;
    trial_end_iso8601_timestamp: string;
    trial_index: number;
    present_shapes: Shape[];
    response_shapes: Shape[];
    response_time_duration_ms: number;
    user_response: 'same' | 'different'; // 사용자가 'same' 또는 'different'로 응답
    user_response_correct: boolean;      // 응답 정오 여부
    quit_button_pressed: boolean;
    device_metadata: DeviceMetadata;
};

// 최상위 데이터 구조 타입 정의
type InputData = {
    trials: Trial[];
};

interface FlutterChannel {
    postMessage(message: string): void; // Flutter로 메시지를 전달하는 메서드
}

// 글로벌 window 객체 확장
declare global {
    interface Window {
        FlutterChannel?: FlutterChannel; // Flutter WebView와의 통신 채널
    }
}

function sendDataToFlutter(data: any) {
    console.log("sendDataToFlutter= " + JSON.stringify(data));

    if (window.FlutterChannel && typeof window.FlutterChannel.postMessage === "function") {
        console.log("FlutterChannel is Defined ");
        window.FlutterChannel.postMessage(JSON.stringify(data));
    } else {
        console.error("FlutterChannel is not defined or postMessage is not a function.");
    }
}

session.onActivityData((ev) => {
    try {
        if (ev.data && Array.isArray(ev.data.trials) && number_of_trials_count == ev.data.trials.length) {
            const sessionData = calculateColorShapeStats(JSON.parse(JSON.stringify(ev.data)));
            console.log("원본데이터= " + JSON.stringify(ev.data));

            // Flutter로 데이터 전달
            sendDataToFlutter({
                type: "complete",
                sessionData: sessionData
            });

        }
    } catch (error) {
        console.error("Error processing activity data:", error);
    }
});

// -----------------------------
// 통계 계산을 위한 유틸 함수들
// -----------------------------
function mean(arr: number[]): number {
    if (arr.length === 0) return 0;
    const sum = arr.reduce((acc: number, cur: number) => acc + cur, 0);
    return sum / arr.length;
}

function standardDeviation(arr: number[]): number {
    if (arr.length === 0) return 0;
    const m = mean(arr);
    // 모집단 표준편차(분산 시 n으로 나눔) 사용
    const variance = arr.reduce((acc: number, cur: number) => acc + (cur - m) ** 2, 0) / arr.length;
    return Math.sqrt(variance);
}

// d-prime 계산에서는 비율이 0 또는 1이면 z-score가 무한대로 발산한다.
// 이를 막기 위해 시행 수(n)를 기준으로 0 -> 0.5 / n, 1 -> (n - 0.5) / n 으로 보정한다.
function correctExtremeRate(rate: number, trialCount: number): number {
    if (rate <= 0) {
        return 0.5 / trialCount;
    }
    if (rate >= 1) {
        return (trialCount - 0.5) / trialCount;
    }
    return rate;
}

// 표준정규분포 inverse CDF, 즉 qnorm 역할을 하는 함수다.
// 저장소에 통계 라이브러리가 없어서 직접 구현했고, Acklam 근사식을 사용한다.
function inverseStandardNormalCDF(probability: number): number {
    if (probability <= 0 || probability >= 1) {
        throw new Error("Probability must be between 0 and 1.");
    }

    const a = [
        -39.69683028665376,
        220.9460984245205,
        -275.9285104469687,
        138.357751867269,
        -30.66479806614716,
        2.506628277459239,
    ];
    const b = [
        -54.47609879822406,
        161.5858368580409,
        -155.6989798598866,
        66.80131188771972,
        -13.28068155288572,
    ];
    const c = [
        -0.007784894002430293,
        -0.3223964580411365,
        -2.400758277161838,
        -2.549732539343734,
        4.374664141464968,
        2.938163982698783,
    ];
    const d = [
        0.007784695709041462,
        0.3224671290700398,
        2.445134137142996,
        3.754408661907416,
    ];
    const low = 0.02425;
    const high = 1 - low;

    if (probability < low) {
        const q = Math.sqrt(-2 * Math.log(probability));
        return (
            (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
            ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
        );
    }

    if (probability <= high) {
        const q = probability - 0.5;
        const r = q * q;
        return (
            (((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q /
            (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1)
        );
    }

    const q = Math.sqrt(-2 * Math.log(1 - probability));
    return -(
        (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
        ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
    );
}

// d-prime은 전체 trial 비율이 아니라 자극 유형별 비율로 계산한다.
// nDiff = hit + miss, nSame = false alarm + correct rejection
// hitRate = hit / nDiff, faRate = false alarm / nSame
// 그리고 d' = qnorm(hitRateCorrected) - qnorm(faRateCorrected) 를 적용한다.
function calculateDPrime(hitCount: number, missCount: number, faCount: number, crCount: number): number {
    const nDiff = hitCount + missCount;
    const nSame = faCount + crCount;

    // SAME 또는 DIFFERENT trial이 하나도 없으면 d-prime을 정의할 수 없으므로 0 처리한다.
    if (nDiff === 0 || nSame === 0) {
        return 0;
    }

    const hitRate = hitCount / nDiff;
    const faRate = faCount / nSame;
    const correctedHitRate = correctExtremeRate(hitRate, nDiff);
    const correctedFaRate = correctExtremeRate(faRate, nSame);

    return (
        inverseStandardNormalCDF(correctedHitRate) -
        inverseStandardNormalCDF(correctedFaRate)
    );
}

// 지표를 담을 결과 구조
type ColorShapeStats = {
    // CScorr 관련
    cscorr_mean: number;   // (정답 비율)
    cscorr_sum: number;    // (총 정답 수)
    cscorr_sd: number;     // (정답 여부 1/0 에 대한 표준편차)

    // CS_error 관련
    cserror_mean: number;  // (오답 비율)
    cserror_sum: number;   // (총 오답 수)
    cserror_sd: number;    // (오답 여부 1/0 에 대한 표준편차)

    // Signal Detection용 분류(FA, Hit, Miss, CR)
    falseAlarm_proportion: number;
    falseAlarm_total: number;
    hit_proportion: number;
    hit_total: number;
    miss_proportion: number;
    miss_total: number;
    correctRejection_proportion: number;
    correctRejection_total: number;

    // 반응 시간
    responseTime_mean: number;
    responseTime_sd: number;

    // 품질 점검
    qualityControl: 0 | 1;

    // 추가 예시 지표 (필요 시)
    cs_HITper_minus_Fa?: number;      // 예: Hit 비율 - False Alarm 비율
    cs_Faper_over_1minusPr?: number;  // 예: falseAlarm_proportion / (1 - correctRejection_proportion)
    cs_advanced_score?: number;       // 예: CSrt_mn+(CSrt_sd/CS_errorsd)*CS_errmn
    dPrime?: number;
};

/**
 * inputData 기준으로
 * CScorr, CS_error, FalseAlarm, Hit, Miss, CorrectRejection 등
 * 모든 통계 지표를 계산하는 함수
 */
function calculateColorShapeStats(inputData: InputData): ColorShapeStats {
    // 1. inputData와 trials 배열 유효성 검사
    if (!inputData || !Array.isArray(inputData.trials)) {
        throw new Error("유효하지 않은 inputData입니다.");
    }
    const trials = inputData.trials;
    const totalTrials = trials.length;

    // 2. trials 배열이 비어있는 경우, 모든 지표를 0으로 반환
    const resultData: any = {
        fAper: 0,
        fAtot: 0,
        hITper: 0,
        hITtot: 0,
        cRejPer: 0,
        cRejtot: 0,
        corrper: 0,
        corrsd: 0,
        corrtot: 0,
        errmn: 0,
        errorsd: 0,
        errtot: 0,
        missPer: 0,
        misstot: 0,
        rtMn: 0,
        rtSd: 0,
        qual: 0,
        br: 0,
        lisas: 0,
        pr: 0,
        dPrime: 0,
        corrCnt: 0,
    }; // 결과 데이터를 저장할 객체

    // 3. CScorr 및 CS_error 배열 생성 (정답은 1, 오답은 0)
    const cscorrArray = trials.map(t => (t.user_response_correct ? 1 : 0));
    const cserrorArray = trials.map(t => (t.user_response_correct ? 0 : 1));

    // 4. 정답과 오답의 합계, 평균, 표준편차 계산
    const cscorr_sum = cscorrArray.reduce((acc: number, cur: number) => acc + cur, 0);
    const cscorr_mean = cscorr_sum / totalTrials;
    const cscorr_sd = standardDeviation(cscorrArray);

    const cserror_sum = cserrorArray.reduce((acc: number, cur: number) => acc + cur, 0);
    const cserror_mean = cserror_sum / totalTrials;
    const cserror_sd = standardDeviation(cserrorArray);

    // 5. False Alarm, Hit, Miss, Correct Rejection 개수 계산
    //    - False Alarm: user_response가 'different'이고 정답이 아닌 경우
    //    - Hit: user_response가 'different'이고 정답인 경우
    //    - Miss: user_response가 'same'이고 정답이 아닌 경우
    //    - Correct Rejection: user_response가 'same'이고 정답인 경우
    let faCount = 0;
    let hitCount = 0;
    let missCount = 0;
    let crCount = 0;

    for (const t of trials) {
        if (t.user_response === 'different' && !t.user_response_correct) {
            faCount++;
        } else if (t.user_response === 'different' && t.user_response_correct) {
            hitCount++;
        } else if (t.user_response === 'same' && !t.user_response_correct) {
            missCount++;
        } else if (t.user_response === 'same' && t.user_response_correct) {
            crCount++;
        }
    }

    // 6. 비율 계산 (전체 시도 수는 0이 아니므로 안전함)
    const falseAlarm_proportion = (faCount / totalTrials) * 100;
    const hit_proportion = (hitCount / totalTrials) * 100;
    const miss_proportion = (missCount / totalTrials) * 100;
    const correctRejection_proportion = (crCount / totalTrials) * 100;
    // 기존 fAper/hITper/missPer/cRejPer는 전체 trial 수를 분모로 둔 퍼센트 값이다.
    // d-prime만큼은 클라이언트 요구사항에 맞게 hit/miss/fa/cr 카운트에서
    // SAME, DIFFERENT 자극 수를 각각 다시 구해 type-specific rate로 계산한다.
    const dPrime = calculateDPrime(hitCount, missCount, faCount, crCount);

    // 7. 반응 시간 계산 (response_time_duration_ms가 숫자인지 확인)
    const validResponseTimes = trials
        .map(t => t.response_time_duration_ms)
        .filter(time => typeof time === 'number');
    const responseTime_mean = validResponseTimes.length > 0 ? mean(validResponseTimes) : 0;
    const responseTime_sd = validResponseTimes.length > 0 ? standardDeviation(validResponseTimes) : 0;

    // 8. 품질 관리: 정해진 횟수(5회)가 모두 제출되면 1, 누락이 있으면 0
    const qualityControl: 0 | 1 = trials.length === number_of_trials_count ? 1 : 0;

    // 9. 추가 지표 계산
    //    (a) hit 비율과 false alarm 비율의 차이
    const cs_HITper_minus_Fa = hit_proportion - falseAlarm_proportion;
    //    (b) false alarm 비율을 (1 - correct rejection 비율)로 나눈 값 (분모가 0이면 0으로 처리)
    const cs_Faper_over_1minusPr = (1 - correctRejection_proportion) === 0
        ? 0
        : (falseAlarm_proportion / (1 - correctRejection_proportion));
    //    (c) advanced score 계산: 오답 표준편차가 0이면 반응 시간 평균, 아니면 공식 적용
    const cs_advanced_score = cserror_sd === 0
        ? responseTime_mean
        : responseTime_mean + (responseTime_sd / cserror_sd) * cserror_mean;

    // 10. 결과 데이터를 담은 객체 생성

    resultData.fAper = falseAlarm_proportion         // False Alarm 비율
    resultData.fAtot = faCount                       // False Alarm 총 개수
    resultData.hITper = hit_proportion               // Hit 비율
    resultData.hITtot = hitCount                     // Hit 총 개수
    resultData.cRejPer = correctRejection_proportion // Correct Rejection 비율
    resultData.cRejtot = crCount                     // Correct Rejection 총 개수
    resultData.corrper = cscorr_mean                 // 정답 비율
    resultData.corrsd = cscorr_sd                    // 정답 표준편차
    resultData.corrtot = cscorr_sum                  // 정답 총 개수
    resultData.errmn = cserror_mean                  // 오답 비율
    resultData.errorsd = cserror_sd                  // 오답 표준편차
    resultData.errtot = cserror_sum                  // 오답 총 개수
    resultData.missPer = miss_proportion             // Miss 비율
    resultData.misstot = missCount                   // Miss 총 개수
    resultData.rtMn = responseTime_mean              // 반응 시간 평균
    resultData.rtSd = responseTime_sd                // 반응 시간 표준편차
    resultData.qual = qualityControl                 // 품질 점검 결과 (1: 정상, 0: 비정상)
    resultData.br = cs_Faper_over_1minusPr           // false alarm 비율 / (1 - correct rejection 비율)
    resultData.lisas = cs_advanced_score             // advanced score
    resultData.pr = cs_HITper_minus_Fa               // hit 비율 - false alarm 비율
    // 기존 pr는 유지하고, 새 지표는 별도 필드 dPrime으로 내려준다.
    resultData.dPrime = dPrime                       // signal detection d-prime
    resultData.corrCnt = cscorr_sum                  // 정답 개수

    return resultData;
}


// 필수: 세션 초기화
session.initialize();
